import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoices as api } from '@/api/endpoints';
import type { Invoice, InvoiceDirection, InvoiceMeta, ManualInvoiceInput } from '@/types';
import { hasPermission } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { formatCurrency } from '@/lib/money';

/** Dokładna data i godzina (do minuty) w formacie polskim, np. „10.07.2026, 14:32". */
const fmtDateTime = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Faktury (Finanse, V84): pobrane z KSeF (zakup/sprzedaż), z filtrem statusu płatności. Ręczna
 * synchronizacja z KSeF, szybkie oznaczanie opłaconych, ustawienia KSeF (OWNER/ADMIN).
 * Kliknięcie wiersza otwiera podgląd faktury (/faktury/:id) — tam też zmiana statusu płatności.
 */
export function FakturyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canConfig = hasPermission(user, 'ADMIN_CONFIG');
  const canManage = hasPermission(user, 'FINANCE_MANAGE');

  const [direction, setDirection] = useState<InvoiceDirection>('PURCHASE');
  const [status, setStatus] = useState<string>('');
  const [rows, setRows] = useState<Invoice[] | null>(null);
  const [meta, setMeta] = useState<InvoiceMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const reload = () => api.list(direction, status || undefined).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  const refreshMeta = () => api.meta().then(setMeta).catch(() => {});
  useEffect(() => { setRows(null); setErr(null); reload(); /* eslint-disable-next-line */ }, [direction, status]);
  useEffect(() => { refreshMeta(); }, []);

  const sync = () => {
    setBusy(true); setErr(null);
    api.sync(14).then(r => {
      setBusy(false);
      // KSeF wyłączony → backend zapisuje SKIPPED i zwraca 0/0; nie udawaj „pobrano 0 faktur".
      if (meta?.ksefMode === 'off') {
        alert('Synchronizacja pominięta — integracja KSeF jest wyłączona (tryb „off"). Włącz ją w Ustawieniach KSeF.');
      } else {
        alert(`Pobrano ${r.purchase + r.sales} nowych faktur (${r.purchase} zakup, ${r.sales} sprzedaż).`);
      }
      reload(); refreshMeta();   // odśwież też datę „ostatniej synchronizacji"
    }).catch(e => { setBusy(false); setErr(e?.message ?? String(e)); refreshMeta(); });
  };

  // Szybkie oznaczenie opłacenia (jedno kliknięcie na liście) — bez otwierania modala.
  const togglePaid = (inv: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setTogglingId(inv.id);
    api.togglePaid(inv.id)
      .then(() => { setTogglingId(null); reload(); })
      .catch(er => { setTogglingId(null); setErr(er?.message ?? String(er)); });
  };

  // „Ostatnia synchronizacja" = ostatnie UDANE pobranie; osobno sygnalizujemy nieudaną/pominiętą ostatnią próbę.
  const ls = meta?.lastSync ?? null;
  const successAt = fmtDateTime(ls?.successAt);
  const attemptAt = fmtDateTime(ls?.at);
  const lastFailed = ls?.status === 'ERROR';
  const lastSkipped = ls?.status === 'SKIPPED';
  const attemptNote = lastFailed
    ? `Ostatnia próba nieudana${attemptAt ? ` (${attemptAt})` : ''}`
    : lastSkipped
      ? `Pominięto — KSeF wyłączony${attemptAt ? ` (${attemptAt})` : ''}`
      : null;

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Faktury</h1>
          <div className="text-ink-400 text-[13px]">
            Faktury z KSeF — status płatności {meta && <span className="ml-1 pill pill-earth text-[10px]">KSeF: {meta.ksefMode}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right leading-tight mr-1" title="Synchronizacja automatyczna (co 4 h) oraz ręcznie przyciskiem">
            <div className="text-ink-400 text-[10px] uppercase tracking-wide">Ostatnia synchronizacja</div>
            {successAt ? (
              <div className="text-[11px] text-ink-600 whitespace-nowrap">
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle bg-green-500"/>
                {successAt}
                {ls?.status === 'OK' && (
                  <span className="text-ink-400"> · {ls.trigger === 'MANUAL' ? 'ręczna' : 'automatyczna'}</span>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-ink-400">jeszcze nie zsynchronizowano</div>
            )}
            {attemptNote && (
              <div className={'text-[11px] whitespace-nowrap ' + (lastFailed ? 'text-red-500' : 'text-ink-400')}
                title={ls?.message ?? undefined}>
                ⚠ {attemptNote}
              </div>
            )}
          </div>
          {canConfig && (
            <Link to="/admin/konfiguracja?tab=KSEF" className="btn btn-secondary btn-sm" title="Konfiguracja KSeF w panelu administracyjnym">
              <Icon name="settings" className="w-3.5 h-3.5"/> Ustawienia KSeF
            </Link>
          )}
          <button disabled={busy} onClick={sync} className="btn btn-secondary btn-sm">
            <Icon name="cloud" className="w-3.5 h-3.5"/> {busy ? 'Pobieranie…' : 'Synchronizuj z KSeF'}
          </button>
          {canManage && (
            <button onClick={() => setAdding(true)} className="btn btn-primary btn-sm"
                    title="Faktura spoza KSeF — zagraniczna albo papierowa">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj fakturę
            </button>
          )}
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-head flex-wrap gap-2">
          <div className="flex gap-1 bg-ink-50 p-1 rounded-md">
            <button onClick={() => setDirection('PURCHASE')} className={'btn btn-sm ' + (direction === 'PURCHASE' ? 'btn-primary' : 'btn-ghost')}>Zakupowe</button>
            <button onClick={() => setDirection('SALES')} className={'btn btn-sm ' + (direction === 'SALES' ? 'btn-primary' : 'btn-ghost')}>Sprzedażowe</button>
          </div>
          <div className="w-56 ml-auto">
            <SearchableSelect value={status} onChange={setStatus} placeholder="Wszystkie statusy">
              <option value="">Wszystkie statusy</option>
              {(meta?.paymentStatuses ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchableSelect>
          </div>
        </div>
        <div className="card-body !p-0">
          {rows == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-6 text-center text-ink-400 text-[12px]">Brak faktur. Kliknij „Synchronizuj z KSeF".</div>}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>{direction === 'PURCHASE' ? 'Sprzedawca' : 'Nabywca'}</th>
                  <th>Wystawiona</th>
                  <th>Termin</th>
                  <th className="text-right">Brutto</th>
                  <th className="text-right">Pozostało</th>
                  <th>Status</th>
                  <th className="text-right">Opłacona</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(inv => {
                  const party = direction === 'PURCHASE' ? inv.sellerName : inv.buyerName;
                  const overdue = inv.dueDate && inv.remainingAmount > 0 && inv.dueDate < new Date().toISOString().slice(0, 10);
                  return (
                    <tr key={inv.id} className="hover:bg-ink-50 cursor-pointer" title="Otwórz podgląd faktury"
                        onClick={() => navigate(`/faktury/${inv.id}`)}>
                      <td className="font-mono text-[12px] text-ink-800">{inv.invoiceNumber ?? '—'}</td>
                      {/* max-width na <td> jest ignorowane w table-layout:auto — ogranicza dopiero blok wewnątrz komórki */}
                      <td className="text-[13px] text-ink-800">
                        <div className="truncate max-w-[240px]" title={party ?? undefined}>{party ?? '—'}</div>
                      </td>
                      <td className="font-mono text-[12px] text-ink-500">{inv.issueDate ?? '—'}</td>
                      <td className={'font-mono text-[12px] ' + (overdue ? 'text-red-600 font-semibold' : 'text-ink-500')}>{inv.dueDate ?? '—'}</td>
                      <td className="text-right font-mono text-[13px] text-ink-900">{formatCurrency(inv.totalGross, inv.currency)}</td>
                      <td className="text-right font-mono text-[12px] text-ink-600">{inv.remainingAmount > 0 ? formatCurrency(inv.remainingAmount, inv.currency) : '—'}</td>
                      <td><span className={'pill pill-' + inv.paymentStatusColor}>{inv.paymentStatusName}</span></td>
                      <td className="text-right">
                        {/* Anulowane/sporne — bez szybkiej akcji (zmiana tylko przez okno statusu). */}
                        {inv.paymentStatus !== 'ANULOWANA' && inv.paymentStatus !== 'SPORNA' && (
                          <button
                            onClick={e => togglePaid(inv, e)}
                            disabled={togglingId === inv.id}
                            title={inv.paymentStatus === 'OPLACONA' ? 'Cofnij oznaczenie — do zapłaty' : 'Oznacz jako opłaconą (dziś, pełna kwota)'}
                            className={'btn btn-sm ' + (inv.paymentStatus === 'OPLACONA' ? 'btn-ghost text-green-600' : 'btn-secondary')}>
                            <Icon name="check" className="w-3.5 h-3.5"/>
                            {inv.paymentStatus === 'OPLACONA' ? 'Opłacona' : 'Opłać'}
                          </button>
                        )}
                      </td>
                      <td><Icon name="chevron" className="w-3 h-3 text-ink-400"/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {adding && (
        <NewInvoiceModal
          direction={direction}
          onClose={() => setAdding(false)}
          onSaved={id => { setAdding(false); navigate(`/faktury/${id}`); }}
        />
      )}
    </div>
  );
}

/**
 * Faktura wprowadzana ręcznie (V153) — domyka lukę, przez którą `InvoiceSource.MANUAL` istniał
 * w enumie, ale nic go nie zapisywało. Skan dokłada się na ekranie faktury (karta „Pliki faktury").
 */
function NewInvoiceModal({ direction, onClose, onSaved }: {
  direction: InvoiceDirection;
  onClose: () => void;
  onSaved: (invoiceId: number) => void;
}) {
  const [form, setForm] = useState<ManualInvoiceInput>({
    direction,
    invoiceNumber: '',
    issueDate: new Date().toISOString().slice(0, 10),
    saleDate: null,
    dueDate: null,
    sellerNip: null,
    sellerName: '',
    buyerNip: null,
    buyerName: null,
    ourCompanyId: null,
    counterpartyClientId: null,
    currency: 'PLN',
    totalNet: null,
    totalVat: null,
    totalGross: 0,
    notes: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof ManualInvoiceInput>(k: K, v: ManualInvoiceInput[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const isPurchase = form.direction === 'PURCHASE';

  const save = () => {
    setBusy(true); setErr(null);
    api.createManual(form)
      .then(inv => onSaved(inv.id))
      .catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div>
            <div className="font-semibold text-[14px]">Nowa faktura (spoza KSeF)</div>
            <div className="text-[11px] text-ink-400">Dostawcy zagraniczni i dokumenty papierowe</div>
          </div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Rodzaj</span>
              <SearchableSelect value={form.direction} onChange={v => set('direction', v as InvoiceDirection)}>
                <option value="PURCHASE">Zakupowa (otrzymana)</option>
                <option value="SALES">Sprzedażowa (wystawiona)</option>
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Numer faktury *</span>
              <input className="input" value={form.invoiceNumber} onChange={e => set('invoiceNumber', e.target.value)}
                     placeholder="np. INV-2026-00412"/></label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">{isPurchase ? 'Sprzedawca' : 'Nabywca'}</span>
              <input className="input"
                     value={(isPurchase ? form.sellerName : form.buyerName) ?? ''}
                     onChange={e => set(isPurchase ? 'sellerName' : 'buyerName', e.target.value)}/></label>
            <label className="field"><span className="field-label">NIP / VAT UE</span>
              <input className="input"
                     value={(isPurchase ? form.sellerNip : form.buyerNip) ?? ''}
                     onChange={e => set(isPurchase ? 'sellerNip' : 'buyerNip', e.target.value)}/></label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Wystawiona</span>
              <input type="date" className="input" value={form.issueDate ?? ''}
                     onChange={e => set('issueDate', e.target.value || null)}/></label>
            <label className="field"><span className="field-label">Sprzedaż</span>
              <input type="date" className="input" value={form.saleDate ?? ''}
                     onChange={e => set('saleDate', e.target.value || null)}/></label>
            <label className="field"><span className="field-label">Termin płatności</span>
              <input type="date" className="input" value={form.dueDate ?? ''}
                     onChange={e => set('dueDate', e.target.value || null)}/></label>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <label className="field"><span className="field-label">Netto</span>
              <input type="number" step="0.01" className="input" value={form.totalNet ?? ''}
                     onChange={e => set('totalNet', e.target.value === '' ? null : Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">VAT</span>
              <input type="number" step="0.01" className="input" value={form.totalVat ?? ''}
                     onChange={e => set('totalVat', e.target.value === '' ? null : Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">Brutto *</span>
              <input type="number" step="0.01" min="0" className="input" value={form.totalGross}
                     onChange={e => set('totalGross', Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">Waluta</span>
              <input className="input uppercase" maxLength={3} value={form.currency}
                     onChange={e => set('currency', e.target.value.toUpperCase())}/></label>
          </div>

          <label className="field"><span className="field-label">Notatki</span>
            <textarea className="input" rows={2} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)}/></label>

          <div className="field-hint">
            Po zapisaniu otworzy się podgląd faktury — tam wgrasz skan w karcie „Pliki faktury".
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy || !form.invoiceNumber.trim()} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}
